import { useEffect, useMemo, useState } from 'react';
import { CheckOutlined, TeamOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Input, Modal, Segmented, Select, Table, Tag, message } from 'antd';
import {
  assignJobPositions,
  getJobPositionMatching,
} from '@/services/jobPositions.service';
import { JOB_POSITIONS_QUERY_KEY, useJobPositionsT } from './useJobPositions';

const CONFIDENCE_COLORS = { high: 'green', medium: 'gold', low: 'orange', none: 'default' };

const fold = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd');

/**
 * "Match employees": every active employee with the position typed today and
 * the catalog position suggested for it. A row is confirmed on its own (the
 * choice can be changed first), or all safe suggestions at once. Saving goes
 * through the ordinary employee update, so history records it.
 */
export default function JobPositionMatchingModal({ companyId, open, onClose, positions, onAssigned }) {
  const { tj } = useJobPositionsT();
  const [filter, setFilter] = useState('open');
  const [search, setSearch] = useState('');
  // employeeId → chosen catalog position, where it differs from the suggestion.
  const [choice, setChoice] = useState({});

  const query = useQuery({
    queryKey: [JOB_POSITIONS_QUERY_KEY, companyId, 'matching'],
    queryFn: () => getJobPositionMatching(companyId),
    enabled: open && !!companyId,
    retry: false,
  });
  useEffect(() => {
    if (open) setChoice({});
  }, [open]);

  const assign = useMutation({
    mutationFn: (assignments) => assignJobPositions(companyId, assignments),
    onSuccess: (result, assignments) => {
      setChoice((prev) => {
        const next = { ...prev };
        assignments.forEach((a) => delete next[a.employeeId]);
        return next;
      });
      if (result.failed) message.warning(tj('matchPartly', { updated: result.updated, failed: result.failed }));
      else message.success(tj('matchSaved', { count: result.updated + result.unchanged }));
      query.refetch();
      onAssigned?.();
    },
    onError: (error) => message.error(error?.response?.data?.message || tj('saveFailed')),
  });

  const options = useMemo(
    () => (positions || []).map((p) => ({ value: p._id, label: p.nameSr })),
    [positions]
  );

  const rows = query.data?.rows || [];
  const chosenOf = (row) => choice[row._id] ?? row.jobPosition ?? row.suggestion?._id ?? null;
  const safeRows = rows.filter(
    (r) => !r.jobPosition && r.confidence === 'high' && r.suggestion && !(r._id in choice)
  );

  const shown = rows.filter((r) => {
    if (filter === 'open' && r.jobPosition) return false;
    if (filter === 'assigned' && !r.jobPosition) return false;
    if (!search) return true;
    const q = fold(search);
    return [r.firstName, r.lastName, r.position, r.sectorName].some((v) => fold(v).includes(q));
  });

  const confirm = (list) =>
    assign.mutate(list.map((r) => ({ employeeId: r._id, jobPositionId: chosenOf(r) })).filter((a) => a.jobPositionId));

  const columns = [
    {
      title: tj('matchEmployee'),
      key: 'name',
      width: 190,
      render: (_, r) => (
        <div>
          <div className="font-semibold">{`${r.firstName} ${r.lastName}`}</div>
          {r.sectorName && <div className="text-xs text-zinc-400">{r.sectorName}</div>}
        </div>
      ),
    },
    {
      title: tj('matchCurrent'),
      dataIndex: 'position',
      key: 'position',
      render: (text) => text || <span className="text-zinc-400">—</span>,
    },
    {
      title: tj('matchCatalog'),
      key: 'catalog',
      width: 320,
      render: (_, r) => (
        <Select
          showSearch
          allowClear
          className="w-full"
          optionFilterProp="label"
          value={chosenOf(r)}
          placeholder={tj('matchPick')}
          options={options}
          onChange={(value) => setChoice((prev) => ({ ...prev, [r._id]: value ?? null }))}
        />
      ),
    },
    {
      title: tj('matchConfidence'),
      key: 'confidence',
      width: 120,
      render: (_, r) =>
        r.jobPosition && !(r._id in choice) ? (
          <Tag color="blue">{tj('matchAssigned')}</Tag>
        ) : (
          <Tag color={CONFIDENCE_COLORS[r.confidence]}>{tj(`confidence.${r.confidence}`)}</Tag>
        ),
    },
    {
      key: 'action',
      width: 110,
      render: (_, r) => {
        const chosen = chosenOf(r);
        const unchanged = r.jobPosition && String(chosen) === String(r.jobPosition);
        return (
          <Button
            size="small"
            className="!rounded-[50px]"
            icon={<CheckOutlined />}
            disabled={!chosen || unchanged}
            loading={assign.isPending && assign.variables?.some((a) => a.employeeId === r._id)}
            onClick={() => confirm([r])}
          >
            {tj('matchConfirm')}
          </Button>
        );
      },
    },
  ];

  const s = query.data?.summary;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1180}
      footer={null}
      title={
        <span className="flex items-center gap-2">
          <TeamOutlined /> {tj('matchTitle')}
        </span>
      }
    >
      <p className="mb-3 text-[13px] text-zinc-500">{tj('matchHint')}</p>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Segmented
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'open', label: tj('matchFilterOpen', { count: s ? s.total - s.assigned : 0 }) },
            { value: 'assigned', label: tj('matchFilterAssigned', { count: s?.assigned || 0 }) },
            { value: 'all', label: tj('matchFilterAll', { count: s?.total || 0 }) },
          ]}
        />
        <Input.Search
          allowClear
          className="!w-64"
          placeholder={tj('search')}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex-1" />
        <Button
          type="primary"
          className="!rounded-[50px]"
          icon={<CheckOutlined />}
          disabled={!safeRows.length}
          loading={assign.isPending}
          onClick={() =>
            Modal.confirm({
              title: tj('matchConfirmSafeTitle', { count: safeRows.length }),
              content: tj('matchConfirmSafeText'),
              okText: tj('matchConfirm'),
              onOk: () => confirm(safeRows),
            })
          }
        >
          {tj('matchConfirmSafe', { count: safeRows.length })}
        </Button>
      </div>
      <Table
        size="small"
        rowKey="_id"
        loading={query.isLoading}
        dataSource={shown}
        columns={columns}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        scroll={{ y: 520 }}
      />
    </Modal>
  );
}
